import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCartQueryKey, useCartQuery } from '../../hooks/useCartQuery';
import ClientLayout from '../../layouts/ClientLayout';
import { authService } from '../../lib/api/apiService';
import { addressService } from '../../lib/api/addressService';
import { cartAPI } from '../../api/endpoints';
import { useCart } from '../../context/CartContext';
import { PRODUCT_PLACEHOLDER_IMAGE } from '../../utils/constants';
import {
  getCartItemsArrayFromCartApiResponse,
  normalizeServerItemToCartItem,
  parseStock,
  resolveCartItemImageSrc,
} from '../../utils/productHelpers';
import { computeCheckoutTotal } from '../../utils/checkoutTotals';
import useCartOperations from '../../hooks/useCartOperations';
import useCurrentUser from '../../hooks/useCurrentUser';
import { promoService } from '../../lib/api/shopService';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

const guestCartMergedStorageKey = (uid) => `kilimmanjaro_guest_cart_merged_${uid}`;

const cartLineIdentityKey = (item) =>
  `${String(item.productId)}|${item.variantId ?? ''}|${item.optionKey ?? ''}`;

const formatFcfa = (value) => Number(value ?? 0).toLocaleString('fr-FR');

const CartQuantityControl = ({ item, isLoadingCart, onUpdate }) => {
  // stock peut valoir 0 (rupture = valeur valide) ; null = inconnu.
  const stock = parseStock(item?.stock);
  const atMax = stock !== null && stock > 0 && (item?.quantity ?? 1) >= stock;
  return (
  <div className="relative inline-flex flex-col items-center">
  <div className="flex items-center border border-gray-300 rounded-full">
    <button
      type="button"
      onClick={() =>
        onUpdate(
          item.productId,
          item.variantId ?? null,
          item.optionKey ?? null,
          Math.max(1, (item?.quantity ?? 1) - 1)
        )
      }
      disabled={isLoadingCart}
      className="px-2.5 sm:px-3 py-1 text-gray-600 hover:text-gray-900 rounded-l-full disabled:opacity-50 disabled:cursor-not-allowed"
      aria-label="Diminuer la quantité"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5 12h14" />
      </svg>
    </button>
    <input
      type="number"
      value={item?.quantity ?? 1}
      onChange={(e) =>
        onUpdate(
          item.productId,
          item.variantId ?? null,
          item.optionKey ?? null,
          parseInt(e.target.value, 10) || 1
        )
      }
      disabled={isLoadingCart}
      className="w-10 sm:w-12 text-center border-none focus:ring-0 text-sm bg-transparent focus:outline-none disabled:opacity-50"
      min="1"
      max={stock ?? undefined}
      aria-label="Quantité"
    />
    <button
      type="button"
      onClick={() =>
        onUpdate(
          item.productId,
          item.variantId ?? null,
          item.optionKey ?? null,
          (item?.quantity ?? 1) + 1
        )
      }
      disabled={isLoadingCart || atMax}
      className="px-2.5 sm:px-3 py-1 text-gray-600 hover:text-gray-900 rounded-r-full disabled:opacity-50 disabled:cursor-not-allowed"
      aria-label="Augmenter la quantité"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5 12h14" />
        <path d="M12 5v14" />
      </svg>
    </button>
  </div>
  {atMax && (
    <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-32 text-center text-[11px] leading-tight text-amber-600 font-medium">
      Quantité max : {stock} disponible{stock > 1 ? 's' : ''}
    </span>
  )}
  </div>
  );
};

const CartRemoveButton = ({ isLoadingCart, onRemove }) => (
  <button
    type="button"
    onClick={onRemove}
    disabled={isLoadingCart}
    className="text-red-500 hover:text-red-700 p-2 rounded-full bg-red-50 hover:bg-red-100 border border-red-200 disabled:opacity-50 shrink-0"
    aria-label="Retirer du panier"
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  </button>
);

const CartItemCardMobile = ({ item, isLoadingCart, onUpdate, onRemove }) => {
  const lineTotal = (item?.price ?? 0) * (item?.quantity ?? 0);

  return (
    <article className="p-4 border-b border-gray-100 last:border-b-0">
      <div className="flex gap-3">
        <img
          src={resolveCartItemImageSrc(item)}
          alt={item?.name}
          className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg shrink-0"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            e.target.src = PRODUCT_PLACEHOLDER_IMAGE;
          }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-2 pr-1">
              {item?.name}
            </h3>
            <CartRemoveButton isLoadingCart={isLoadingCart} onRemove={onRemove} />
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {formatFcfa(item?.price ?? 0)} Fcfa
          </p>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
            <CartQuantityControl item={item} isLoadingCart={isLoadingCart} onUpdate={onUpdate} />
            <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
              {formatFcfa(lineTotal)} Fcfa
            </p>
          </div>
        </div>
      </div>
    </article>
  );
};

// Affiche un item en rupture/indisponible en conservant les infos clés
// (photo, nom, prix, quantité, total) avec une apparence grisée et un badge
// "n'est plus disponible". Les contrôles de quantité sont supprimés et le
// bouton de suppression reste actif pour permettre de retirer l'item.
const CartUnavailableCardMobile = ({ item, isLoadingCart, t, onRemove }) => {
  const lineTotal = (item?.price ?? 0) * (item?.quantity ?? 0);

  return (
    <article className="p-4 bg-gray-50 border-b border-gray-100">
      <div className="flex gap-3">
        <div className="relative shrink-0">
          <img
            src={resolveCartItemImageSrc(item)}
            alt={item?.name}
            className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg grayscale opacity-70"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              e.target.src = PRODUCT_PLACEHOLDER_IMAGE;
            }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-gray-700 text-sm sm:text-base line-clamp-2 pr-1">
              {item?.name}
            </h3>
            <CartRemoveButton isLoadingCart={isLoadingCart} onRemove={onRemove} />
          </div>
          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
            {t('cart.product.unavailable', "Ce produit n'est plus disponible")}
          </span>
          <p className="text-sm text-gray-500 mt-2">
            {formatFcfa(item?.price ?? 0)} Fcfa
          </p>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
            <span className="text-sm text-gray-500">
              Qté : <span className="font-medium text-gray-700">{item?.quantity ?? 0}</span>
            </span>
            <p className="text-sm font-semibold text-gray-500 whitespace-nowrap">
              {formatFcfa(lineTotal)} Fcfa
            </p>
          </div>
        </div>
      </div>
    </article>
  );
};

const DeliveryModeModal = ({
  isOpen,
  onClose,
  options,
  selectedId,
  onSelect,
  onAddAddress,
  isAdding,
  isFetchingLocation,
  newAddressName,
  onNewAddressNameChange,
  onCreateAddress,
  isSavingAddress,
  onFinalize,
  locationError,
  addressSaveError,
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4">
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('cart.delivery_modal.title')}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 transition hover:text-gray-600"
            aria-label={t('cart.delivery_modal.close')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {options.map((option) => {
            const isActive = option.id === selectedId;
            return (
              <label
                key={option.id}
                className={`flex cursor-pointer flex-col rounded-xl border px-4 py-3 text-sm transition hover:border-orange-500 ${isActive ? 'border-orange-500 bg-orange-50' : 'border-gray-200'
                  }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="delivery-mode"
                    value={option.id}
                    checked={isActive}
                    onChange={() => onSelect(option.id)}
                    className="mt-1 h-4 w-4 border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                  <div className="flex-1 space-y-1">
                    <p className="font-medium text-gray-900">{option.label}</p>
                    {option.address && (
                      <p className="text-gray-600">{option.address}</p>
                    )}
                    {typeof option.cost === 'number' && (
                      <span className={`text-xs font-semibold ${option.cost === 0 ? 'text-green-600' : 'text-orange-500'}`}>
                        {option.cost === 0
                          ? '+ 0 FCFA'
                          : `+ ${option.cost.toLocaleString()} FCFA`}
                      </span>
                    )}
                  </div>
                </div>
              </label>
            );
          })}

          {isAdding && (
            <div className="rounded-xl border border-dashed border-orange-300 bg-orange-50 px-4 py-3 text-sm text-gray-700">
              {isFetchingLocation ? (
                <p>{t('cart.delivery_modal.locating')}</p>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('cart.delivery_modal.place_name')}
                    </label>
                    <input
                      type="text"
                      value={newAddressName}
                      onChange={(e) => onNewAddressNameChange(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200"
                      placeholder={t('cart.delivery_modal.place_name_placeholder')}
                    />
                  </div>

                  {(locationError || addressSaveError) && (
                    <p className="text-xs text-red-500">
                      {addressSaveError || locationError}
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={onCreateAddress}
                    disabled={
                      isSavingAddress ||
                      isFetchingLocation ||
                      !newAddressName.trim()
                    }
                    className={`w-[fit-content] rounded-full px-4 py-1 text-sm font-medium text-white transition ${isSavingAddress || isFetchingLocation || !newAddressName.trim()
                        ? 'cursor-not-allowed bg-gray-400'
                        : 'bg-blue-700 hover:bg-blue-800'
                      }`}
                  >
                    {isSavingAddress ? t('cart.delivery_modal.adding') : t('cart.delivery_modal.add')}
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={onAddAddress}
            className="text-left text-sm font-medium text-orange-500 transition hover:text-orange-600"
          >
            {t('cart.delivery_modal.add_address')}
          </button>
        </div>

        <div className="rounded-b-2xl border-t px-6 py-4">
          <button
            onClick={onFinalize}
            disabled={isFetchingLocation || isSavingAddress}
            className={`w-full rounded-full py-2.5 text-sm font-semibold text-white transition ${isFetchingLocation || isSavingAddress
                ? 'cursor-not-allowed bg-gray-400'
                : 'bg-orange-hover hover:bg-orange-600'
              }`}
          >
            {t('cart.delivery_modal.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

const Cart = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { items: cartItems, updateQuantity, removeItem, refetchCart } = useCart();
  const hasMergedGuestCartRef = useRef(false);

  // Tous les hooks doivent être déclarés AVANT tout return conditionnel
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
  /** Invité : point de retrait seul ; connecté : rempli depuis l’API (comme Checkout). */
  const [deliveryOptions, setDeliveryOptions] = useState(() => [
    {
      id: 'pickup',
      label: t('cart.delivery_modal.pickup_point'),
      address: null,
      cost: 0,
      isDefault: false,
    },
  ]);
  const [selectedDeliveryId, setSelectedDeliveryId] = useState('pickup');
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [newAddressName, setNewAddressName] = useState('');
  const [newAddressFull, setNewAddressFull] = useState('');
  const [newAddressCoords, setNewAddressCoords] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [addressSaveError, setAddressSaveError] = useState('');
  const [isSavingAddress, setIsSavingAddress] = useState(false);

  const { data: user } = useCurrentUser();
  const isAuthenticated = authService.isAuthenticated();
  const userId = user?._id || user?.id;

  // Panier connecté : uniquement la réponse de GET /users/:userId/cart (pas le contexte local)
  const {
    data: cartApiResponse,
    isLoading: isLoadingCartApi,
    error: cartApiError,
  } = useCartQuery(userId, {
    enabled: isAuthenticated && Boolean(userId),
  });

  const displayCartItems = useMemo(() => {
    if (isAuthenticated && userId) {
      return getCartItemsArrayFromCartApiResponse(cartApiResponse)
        .map((serverItem) => normalizeServerItemToCartItem(serverItem))
        .filter(Boolean);
    }
    return cartItems;
  }, [isAuthenticated, userId, cartApiResponse, cartItems]);

  const isLoadingCart = isAuthenticated && userId ? isLoadingCartApi : false;
  const cartError = isAuthenticated && userId ? cartApiError : null;

  const { removeItemFromAPI, updateItemQuantityInAPI } = useCartOperations();

  useEffect(() => {
    if (!isAuthenticated) hasMergedGuestCartRef.current = false;
  }, [isAuthenticated]);

  // Fusion panier invité → serveur : uniquement les lignes absentes du GET (évite un 2e POST si l’article
  // est déjà sur le serveur mais sans itemId local, ex. après useAddToCart).
  useEffect(() => {
    if (!isAuthenticated || !userId) return;
    if (typeof window !== 'undefined' && sessionStorage.getItem(guestCartMergedStorageKey(userId))) {
      hasMergedGuestCartRef.current = true;
      return;
    }
    if (hasMergedGuestCartRef.current) return;
    if (isLoadingCartApi) return;

    const guestItems = cartItems.filter((item) => !item.itemId);
    if (guestItems.length === 0) {
      hasMergedGuestCartRef.current = true;
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(guestCartMergedStorageKey(userId), '1');
      }
      return;
    }

    const raw = getCartItemsArrayFromCartApiResponse(cartApiResponse);
    const serverKeys = new Set(
      (Array.isArray(raw) ? raw : []).map((s) => cartLineIdentityKey(s))
    );
    const toMerge = guestItems.filter(
      (item) => !serverKeys.has(cartLineIdentityKey(item))
    );

    if (toMerge.length === 0) {
      hasMergedGuestCartRef.current = true;
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(guestCartMergedStorageKey(userId), '1');
      }
      return;
    }

    hasMergedGuestCartRef.current = true;
    (async () => {
      try {
        for (const item of toMerge) {
          await cartAPI.addCartItem(userId, {
            productId: String(item.productId),
            quantity: item.quantity,
          });
        }
        if (typeof window !== 'undefined') {
          sessionStorage.setItem(guestCartMergedStorageKey(userId), '1');
        }
        queryClient.invalidateQueries({ queryKey: getCartQueryKey(userId) });
      } catch (err) {
        console.error('Merge panier invité:', err);
        hasMergedGuestCartRef.current = false;
      }
    })();
  }, [
    isAuthenticated,
    userId,
    cartItems,
    cartApiResponse,
    isLoadingCartApi,
    queryClient,
  ]);

  const { data: deliveryAddressesData, isPending: isLoadingDeliveryAddresses } = useQuery({
    queryKey: ['delivery-addresses', userId],
    queryFn: () => addressService.getDeliveryAddresses(userId),
    enabled: isAuthenticated && Boolean(userId),
    refetchOnMount: true,
  });

  const selectDelivery = useCallback(
    (id) => {
      const sid = id === 'pickup' || id == null ? 'pickup' : String(id);
      setSelectedDeliveryId(sid);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (sid === 'pickup') {
            next.delete('deliveryId');
          } else {
            next.set('deliveryId', sid);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setDeliveryOptions([
        {
          id: 'pickup',
          label: t('cart.delivery_modal.pickup_point'),
          address: null,
          cost: 0,
          isDefault: false,
        },
      ]);
      setSelectedDeliveryId('pickup');
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('deliveryId');
          return next;
        },
        { replace: true }
      );
      return;
    }

    if (deliveryAddressesData === undefined) return;

    const raw = deliveryAddressesData?.data ?? deliveryAddressesData;
    const serverAddresses = Array.isArray(raw) ? raw : [];

    const mapped = serverAddresses.map((address) => ({
      id: String(address.id),
      label: address.name || address.label || address.address_line || t('cart.delivery_modal.client_address'),
      address: address.address || '',
      cost: address.cost ?? null,
      isDefault: address.isDefault ?? address.is_default ?? false,
      latitude: address.latitude,
      longitude: address.longitude,
    }));

    setDeliveryOptions(mapped);

    if (mapped.length > 0) {
      const fromUrl = searchParams.get('deliveryId');
      const match = fromUrl && mapped.find((m) => m.id === String(fromUrl));
      if (match) {
        setSelectedDeliveryId(match.id);
      } else {
        if (fromUrl) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete('deliveryId');
              return next;
            },
            { replace: true }
          );
        }
        const defaultAddress =
          serverAddresses.find((addr) => addr.isDefault || addr.is_default) || serverAddresses[0];
        setSelectedDeliveryId(String(defaultAddress?.id ?? mapped[0].id));
      }
    } else {
      setSelectedDeliveryId('pickup');
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('deliveryId');
          return next;
        },
        { replace: true }
      );
    }
  }, [deliveryAddressesData, isAuthenticated, userId, t, searchParams, setSearchParams]);

  const checkoutUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedDeliveryId && selectedDeliveryId !== 'pickup') {
      params.set('deliveryId', selectedDeliveryId);
    }
    if (appliedPromo?.code) {
      params.set('promoCode', appliedPromo.code);
    }
    const qs = params.toString();
    return qs ? `/checkout?${qs}` : '/checkout';
  }, [selectedDeliveryId, appliedPromo?.code]);

  const selectedDelivery = useMemo(() => {
    const found = deliveryOptions.find((option) => option.id === selectedDeliveryId);
    if (found) return found;
    if (selectedDeliveryId === 'pickup') {
      return {
        id: 'pickup',
        label: t('cart.delivery_modal.pickup_point'),
        address: null,
        cost: 0,
        isDefault: false,
      };
    }
    return deliveryOptions[0];
  }, [deliveryOptions, selectedDeliveryId, t]);

  // Calculs (connecté : lignes issues de l’API uniquement)
  const subtotal = displayCartItems.reduce(
    (sum, item) => sum + ((item?.price ?? 0) * (item?.quantity ?? 0)),
    0
  );
  const shippingCost = selectedDelivery?.cost ?? 0;
  const total = computeCheckoutTotal(
    subtotal,
    appliedPromo?.discount,
    shippingCost,
  );

  const cartSignature = useMemo(
    () =>
      (displayCartItems || [])
        .map((item) => `${item.productId}:${item.quantity || 1}`)
        .join('|'),
    [displayCartItems],
  );

  useEffect(() => {
    setAppliedPromo(null);
    setPromoError('');
  }, [cartSignature]);

  const handleApplyPromo = async () => {
    const code = promoInput.trim();
    if (!code) {
      setPromoError(
        t('checkout.errors.promo_required', 'Saisissez un code promo'),
      );
      return;
    }
    if (!displayCartItems?.length) {
      setPromoError(t('checkout.errors.empty_cart', 'Le panier est vide'));
      return;
    }
    setPromoLoading(true);
    setPromoError('');
    try {
      const result = await promoService.validate({
        code,
        items: displayCartItems.map((item) => ({
          productId: String(item.productId),
          quantity: item.quantity || 1,
        })),
        userId: userId || undefined,
      });
      const payload = result?.data ?? result;
      if (!payload?.valid && !payload?.discount) {
        throw new Error(payload?.message || 'Code invalide');
      }
      setAppliedPromo({
        code: payload.code || code.toUpperCase(),
        discount: Number(payload.discount) || 0,
        shopId: payload.shopId || null,
        shopSubtotal: payload.shopSubtotal || 0,
      });
      setPromoInput(payload.code || code.toUpperCase());
      toast.success(
        t('checkout.promo_code.applied', 'Code promo appliqué'),
      );
    } catch (err) {
      setAppliedPromo(null);
      const data = err?.response?.data;
      const apiMessage =
        (typeof data === 'string' && data) ||
        data?.message ||
        (Array.isArray(data?.message) ? data.message.join(', ') : null);
      setPromoError(
        apiMessage ||
          err?.message ||
          t('checkout.errors.promo_invalid', 'Code promo invalide'),
      );
    } finally {
      setPromoLoading(false);
    }
  };

  const clearPromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
    setPromoError('');
  };

  const handleRemoveItem = (productId, variantId, optionKey) => {
    const itemToRemove = displayCartItems.find(
      (item) =>
        item.productId === productId &&
        item.variantId === variantId &&
        item.optionKey === optionKey
    );

    if (itemToRemove?.itemId) {
      removeItemFromAPI(
        itemToRemove.itemId,
        () => {
          // Toujours retirer du contexte local pour que le badge header (totalItems) se mette à jour
          removeItem(productId, variantId, optionKey);
        },
        () => toast.error(t('cart.errors.remove_error'))
      );
    } else {
      removeItem(productId, variantId, optionKey);
    }
  };

  const handleUpdateQuantity = (productId, variantId, optionKey, newQuantity) => {
    const itemToUpdate = displayCartItems.find(
      (item) =>
        item.productId === productId &&
        item.variantId === variantId &&
        item.optionKey === optionKey
    );

    if (!itemToUpdate) {
      console.error('Item non trouvé dans le panier');
      return;
    }

    let validQuantity = Math.max(1, parseInt(newQuantity, 10) || 1);

    // Limiter au stock disponible (0 = rupture, valeur valide ; null = inconnu).
    const stock = parseStock(itemToUpdate.stock);
    if (stock === 0) {
      toast.error("Ce produit n'est plus disponible.");
      return;
    }
    if (stock !== null && validQuantity > stock) {
      validQuantity = stock;
      toast.error(`Seulement ${stock} unité${stock > 1 ? 's' : ''} en stock.`);
    }

    if (itemToUpdate?.itemId) {
      updateItemQuantityInAPI(
        itemToUpdate.itemId,
        validQuantity,
        () => {
          updateQuantity(productId, variantId, optionKey, validQuantity);
        },
        () => toast.error(t('cart.errors.update_error'))
      );
    } else {
      updateQuantity(productId, variantId, optionKey, validQuantity);
    }
  };

  const handleAddAddressClick = () => {
    if (isFetchingLocation) return;

    setIsAddingAddress(true);
    setIsFetchingLocation(true);
    setLocationError('');
    setAddressSaveError('');
    setNewAddressCoords(null);
    setNewAddressName('');
    setNewAddressFull('');

    if (!navigator.geolocation) {
      setLocationError(t('cart.delivery_modal.errors.geolocation_not_supported'));
      setIsFetchingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setNewAddressCoords({ latitude, longitude });

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
            { headers: { 'User-Agent': 'kilimmanjaro-app/1.0' } }
          );

          if (!response.ok) {
            throw new Error(t('cart.delivery_modal.errors.cannot_get_place'));
          }

          const data = await response.json();
          const displayName = data?.display_name || t('cart.delivery_modal.custom_address');
          setNewAddressName(displayName);
          setNewAddressFull(displayName);
          setLocationError('');
        } catch (error) {
          console.error(error);
          setLocationError(t('cart.delivery_modal.errors.cannot_get_location_name'));
          setAddressSaveError('');
          const fallback = t('account.addresses.new_address');
          setNewAddressName(fallback);
          setNewAddressFull(fallback);
        } finally {
          setIsFetchingLocation(false);
        }
      },
      (error) => {
        console.error(error);
        setLocationError(t('cart.delivery_modal.errors.cannot_get_location'));
        setAddressSaveError('');
        const fallback = t('account.addresses.new_address');
        setNewAddressName(fallback);
        setNewAddressFull(fallback);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleCreateAddress = async () => {
    if (!newAddressName.trim()) {
      setAddressSaveError(t('cart.delivery_modal.errors.name_required'));
      return;
    }

    if (!newAddressFull.trim()) {
      setAddressSaveError(t('cart.delivery_modal.errors.address_required'));
      return;
    }

    if (!userId) {
      setAddressSaveError(t('cart.delivery_modal.errors.login_required'));
      return;
    }

    setIsSavingAddress(true);
    setAddressSaveError('');

    try {
      const payload = {
        name: newAddressName.trim(),
        address: newAddressFull.trim(),
        latitude: newAddressCoords?.latitude ?? null,
        longitude: newAddressCoords?.longitude ?? null,
        isDefault: false,
      };

      const rawResponse = await addressService.createDeliveryAddress(
        userId,
        payload
      );

      const createdAddress = rawResponse?.data || rawResponse || {};
      const newId = createdAddress.id;
      if (newId != null && newId !== '') {
        selectDelivery(String(newId));
      }
      await queryClient.invalidateQueries({ queryKey: ['delivery-addresses', userId] });
      setIsAddingAddress(false);
      setNewAddressName('');
      setNewAddressFull('');
      setNewAddressCoords(null);
    } catch (error) {
      console.error(error);
      const apiMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message;
      setAddressSaveError(
        apiMessage || "Impossible d'enregistrer l'adresse. Veuillez réessayer."
      );
    } finally {
      setIsSavingAddress(false);
    }
  };

  const handleSaveDelivery = () => {
    setIsDeliveryModalOpen(false);
    setIsAddingAddress(false);
    setIsFetchingLocation(false);
    setLocationError('');
    setAddressSaveError('');
    setNewAddressFull('');
  };

  const handleCloseModal = () => {
    setIsDeliveryModalOpen(false);
    setIsAddingAddress(false);
    setIsFetchingLocation(false);
    setLocationError('');
    setAddressSaveError('');
    setNewAddressFull('');
  };

  const renderCartStatusMessage = (wrapperClassName) => {
    if (isLoadingCart) {
      return (
        <div className={`flex flex-col items-center justify-center gap-4 text-center ${wrapperClassName}`}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-hover" />
          <p className="text-gray-500">{t('cart.loading.cart')}</p>
        </div>
      );
    }
    if (cartError) {
      return (
        <div className={`flex flex-col items-center justify-center gap-2 text-center ${wrapperClassName}`}>
          <p className="text-red-500 font-medium">{t('cart.errors.load_error')}</p>
          <p className="text-gray-500 text-sm">{t('cart.errors.try_again')}</p>
        </div>
      );
    }
    if (displayCartItems.length === 0) {
      return (
        <p className={`text-center text-gray-500 ${wrapperClassName}`}>
          {t('cart.empty.message')}
        </p>
      );
    }
    return null;
  };

  const renderCartItemActions = (item) => ({
    onRemove: () =>
      handleRemoveItem(
        item.productId,
        item.variantId ?? null,
        item.optionKey ?? null
      ),
    onUpdate: handleUpdateQuantity,
  });

  return (
    <ClientLayout>
      <div className="flex-1 px-4 sm:px-6 md:px-0 md:mx-8 lg:mx-16 mx-auto py-8">
        {/* Fil d'Ariane */}
        <div className="mb-8">
          <nav className="text-sm text-gray-600">
            <Link to="/" className="hover:text-gray-900">{t('cart.breadcrumb.home')}</Link>
            <span className="mx-2">&gt;</span>
            <span className="text-gray-900">{t('cart.breadcrumb.cart')}</span>
          </nav>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Section Produits - 2/3 de la largeur */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Liste mobile (cartes) */}
              <div className="lg:hidden">
                {renderCartStatusMessage('p-8') ||
                  displayCartItems.map((item) => {
                    const rowKey = `${item.productId}-${item.variantId ?? 'default'}-${item.optionKey ?? 'base'}`;
                    const { onRemove, onUpdate } = renderCartItemActions(item);

                    if (item.isUnavailable === true) {
                      return (
                        <CartUnavailableCardMobile
                          key={rowKey}
                          item={item}
                          isLoadingCart={isLoadingCart}
                          t={t}
                          onRemove={onRemove}
                        />
                      );
                    }

                    return (
                      <CartItemCardMobile
                        key={rowKey}
                        item={item}
                        isLoadingCart={isLoadingCart}
                        onUpdate={onUpdate}
                        onRemove={onRemove}
                      />
                    );
                  })}
              </div>

              {/* Tableau desktop */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">
                        {t('cart.table.headers.product')}
                      </th>
                      <th className="text-center py-3 px-3 text-sm font-medium text-gray-700">
                        {t('cart.table.headers.price')}
                      </th>
                      <th className="text-center py-3 px-3 text-sm font-medium text-gray-700">
                        {t('cart.table.headers.quantity')}
                      </th>
                      <th className="text-center py-3 px-3 text-sm font-medium text-gray-700">
                        {t('cart.table.headers.total')}
                      </th>
                      <th className="text-center py-3 px-3 text-sm font-medium text-gray-700">
                        {t('cart.table.headers.action')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {isLoadingCart ? (
                      <tr>
                        <td colSpan={5}>{renderCartStatusMessage('p-12')}</td>
                      </tr>
                    ) : cartError ? (
                      <tr>
                        <td colSpan={5}>{renderCartStatusMessage('p-6')}</td>
                      </tr>
                    ) : displayCartItems.length === 0 ? (
                      <tr>
                        <td colSpan={5}>{renderCartStatusMessage('p-6')}</td>
                      </tr>
                    ) : (
                      displayCartItems.map((item) => {
                        const rowKey = `${item.productId}-${item.variantId ?? 'default'}-${item.optionKey ?? 'base'}`;
                        const { onRemove, onUpdate } = renderCartItemActions(item);
                        const lineTotal = (item?.price ?? 0) * (item?.quantity ?? 0);

                        if (item.isUnavailable === true) {
                          // On garde toutes les colonnes (photo, prix, quantité, total)
                          // mais en grisé, et on ajoute un badge "n'est plus disponible"
                          // sous le nom. Pas de contrôle de quantité (la qté reste
                          // affichée en lecture seule).
                          return (
                            <tr key={rowKey} className="bg-gray-50">
                              <td className="py-4 px-4">
                                <div className="flex items-center gap-4 min-w-0">
                                  <img
                                    src={resolveCartItemImageSrc(item)}
                                    alt={item?.name}
                                    className="w-16 h-16 object-cover rounded-lg shrink-0 grayscale opacity-70"
                                    loading="lazy"
                                    decoding="async"
                                    onError={(e) => {
                                      e.target.src = PRODUCT_PLACEHOLDER_IMAGE;
                                    }}
                                  />
                                  <div className="min-w-0">
                                    <h3 className="font-semibold text-gray-700 text-base line-clamp-2">
                                      {item?.name}
                                    </h3>
                                    <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
                                      {t('cart.product.unavailable', "Ce produit n'est plus disponible")}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className="py-4 px-3 text-center">
                                <span className="text-gray-500 font-medium whitespace-nowrap">
                                  {formatFcfa(item?.price ?? 0)} Fcfa
                                </span>
                              </td>
                              <td className="py-4 px-3 text-center">
                                <span className="text-gray-500 font-medium">
                                  {item?.quantity ?? 0}
                                </span>
                              </td>
                              <td className="py-4 px-3 text-center">
                                <span className="text-gray-500 font-medium whitespace-nowrap">
                                  {formatFcfa(lineTotal)} Fcfa
                                </span>
                              </td>
                              <td className="py-4 px-3 text-center">
                                <div className="flex justify-center">
                                  <CartRemoveButton isLoadingCart={isLoadingCart} onRemove={onRemove} />
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        return (
                          <tr key={rowKey} className="hover:bg-gray-50">
                            <td className="py-4 px-4">
                              <div className="flex items-center gap-4 min-w-0">
                                <img
                                  src={resolveCartItemImageSrc(item)}
                                  alt={item?.name}
                                  className="w-16 h-16 object-cover rounded-lg shrink-0"
                                  loading="lazy"
                                  decoding="async"
                                  onError={(e) => {
                                    e.target.src = PRODUCT_PLACEHOLDER_IMAGE;
                                  }}
                                />
                                <h3 className="font-semibold text-gray-900 text-base line-clamp-2">
                                  {item?.name}
                                </h3>
                              </div>
                            </td>
                            <td className="py-4 px-3 text-center">
                              <span className="text-gray-900 font-medium whitespace-nowrap">
                                {formatFcfa(item?.price ?? 0)} Fcfa
                              </span>
                            </td>
                            <td className="py-4 px-3 text-center">
                              <div className="flex justify-center">
                                <CartQuantityControl
                                  item={item}
                                  isLoadingCart={isLoadingCart}
                                  onUpdate={onUpdate}
                                />
                              </div>
                            </td>
                            <td className="py-4 px-3 text-center">
                              <span className="text-gray-900 font-medium whitespace-nowrap">
                                {formatFcfa(lineTotal)} Fcfa
                              </span>
                            </td>
                            <td className="py-4 px-3 text-center">
                              <div className="flex justify-center">
                                <CartRemoveButton isLoadingCart={isLoadingCart} onRemove={onRemove} />
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="p-4 sm:p-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
                  <h2 className="text-sm sm:text-base font-medium text-gray-900 shrink-0">
                    {t('cart.coupon.label')}
                  </h2>
                  <input
                    type="text"
                    placeholder={t('cart.coupon.placeholder')}
                    value={promoInput}
                    onChange={(e) => {
                      setPromoInput(e.target.value.toUpperCase());
                      setPromoError('');
                    }}
                    disabled={!!appliedPromo}
                    className="w-full flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-orange-light focus:border-transparent text-sm disabled:bg-gray-100"
                  />
                  {appliedPromo ? (
                    <button
                      type="button"
                      onClick={clearPromo}
                      className="w-full sm:w-auto whitespace-nowrap px-6 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-100 transition-colors text-sm"
                    >
                      {t('checkout.promo_code.remove', 'Retirer')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleApplyPromo}
                      disabled={promoLoading}
                      className="w-full sm:w-auto whitespace-nowrap px-6 py-2 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors text-sm disabled:opacity-50"
                    >
                      {promoLoading
                        ? t('checkout.promo_code.applying', 'Vérification…')
                        : t('cart.coupon.apply')}
                    </button>
                  )}
                </div>
                {promoError ? (
                  <p className="mt-2 text-sm text-red-600">{promoError}</p>
                ) : null}
                {appliedPromo ? (
                  <p className="mt-2 text-sm text-green-700">
                    {t('checkout.promo_code.active', 'Code')}{' '}
                    <strong>{appliedPromo.code}</strong>: −{' '}
                    {Number(appliedPromo.discount).toLocaleString('fr-FR')} Fcfa
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          {/* Section Totaux - 1/3 de la largeur */}
          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-6 sticky top-8">
              <h2 className="text-lg font-bold text-gray-900 mb-6">{t('cart.totals.title')}</h2>

              {/* Détails des coûts — même structure / typo que la colonne totaux Checkout */}
              <div className="mb-6 pb-4 border-b border-gray-300">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">{t('cart.totals.subtotal')}</span>
                  <span className="font-medium text-gray-900">{subtotal.toLocaleString()} Fcfa</span>
                </div>
              </div>
              {appliedPromo?.discount > 0 && (
                <div className="mb-6 pb-4 border-b border-gray-300">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">
                      {t('checkout.totals.discount', 'Réduction')} (
                      {appliedPromo.code})
                    </span>
                    <span className="font-medium text-green-700">
                      −{Number(appliedPromo.discount).toLocaleString()} Fcfa
                    </span>
                  </div>
                </div>
              )}
              <div className="mb-6 pb-4 border-b border-gray-300">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">{t('cart.totals.shipping')}</span>
                  <span className="font-medium text-gray-900">{shippingCost.toLocaleString()} Fcfa</span>
                </div>
              </div>

              {/* Adresses de livraison */}
              <div className="mb-6 flex justify-between items-center">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  {t('checkout.totals.delivery_address')}
                </h3>
                {!isAuthenticated ? (
                  <p className="text-sm text-gray-600">
                    {t('checkout.totals.guest_delivery_hint')}
                  </p>
                ) : isLoadingDeliveryAddresses ? (
                  <div className="flex items-center gap-2 py-2 text-sm text-gray-600">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
                    {t('checkout.totals.loading_addresses')}
                  </div>
                ) : deliveryOptions.length === 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      {t('checkout.totals.no_saved_addresses')}
                    </p>
                    <button
                      type="button"
                      onClick={() => setIsDeliveryModalOpen(true)}
                      className="text-left text-sm font-medium text-orange-500 transition hover:text-orange-600"
                    >
                      {t('cart.delivery_modal.add_address')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {deliveryOptions.map((option) => {
                      const isActive = option.id === selectedDeliveryId;

                      return (
                        <div key={option.id} className="flex justify-end items-center gap-2 py-1 text-sm">

                          <label
                            htmlFor={`cart-delivery-${option.id}`}
                            className={`cursor-pointer text-xs ${isActive ? 'font-semibold text-orange-00' : 'font-medium text-gray-600'
                              }`}
                          >
                            {option.label}
                          </label>

                          {option.isDefault && option.latitude && option.longitude ? (
                            <button
                              type="button"
                              onClick={() => {
                                const lat = Number(option.latitude);
                                const lng = Number(option.longitude);
                                window.open(
                                  `https://www.google.com/maps?q=${lat},${lng}`,
                                  '_blank',
                                  'noopener,noreferrer'
                                );
                              }}
                              className="text-blue-600 underline underline-offset-2 hover:text-blue-700 text-xs"
                              title="Voir sur la carte"
                            >
                              (Voir sur le map)
                            </button>
                          ) : null}

                          <input
                            id={`cart-delivery-${option.id}`}
                            type="radio"
                            name="cart-delivery"
                            value={option.id}
                            checked={isActive}
                            onChange={() => selectDelivery(option.id)}
                            className="mt-1 h-4 w-4 shrink-0 border-gray-300 text-orange-500 focus:ring-orange-500"
                          />
                        </div>
                      );
                    })}
                    <div className="flex justify-end mt-8">
                      <button
                        type="button"
                        onClick={() => setIsDeliveryModalOpen(true)}
                        className="text-left text-sm font-medium text-orange-500 transition hover:text-orange-600 mt-6"
                      >
                        {t('cart.delivery_modal.add_address')}
                      </button>
                    </div>

                  </div>
                )}
              </div>

              {/* Total final — aligné sur Checkout (border-y, libellé semi-gras) */}
              <div className="border-y border-gray-300 py-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-base font-semibold text-gray-900">{t('cart.totals.total')}</span>
                  <span className="text-lg font-bold text-gray-900">{total.toLocaleString()} Fcfa</span>
                </div>
              </div>

              {/* Alerte si au moins un produit du panier est en rupture */}
              {displayCartItems.some((it) => it.isUnavailable) && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {t(
                    'cart.totals.has_unavailable',
                    'Certains produits sont en rupture de stock. Retirez-les du panier pour pouvoir passer commande.'
                  )}
                </div>
              )}

              {/* Bouton passer à la caisse */}
              {displayCartItems.some((it) => it.isUnavailable) ? (
                <button
                  type="button"
                  disabled
                  className="w-full bg-gray-300 text-gray-500 py-3 px-6 rounded-full cursor-not-allowed"
                >
                  {t('cart.totals.checkout')}
                </button>
              ) : (
                <Link to={checkoutUrl} onClick={() => refetchCart()}>
                  <button className="w-full bg-orange-hover text-white py-3 px-6 rounded-full hover:bg-orange-700 transition-colors">
                    {t('cart.totals.checkout')}
                  </button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <DeliveryModeModal
        isOpen={isDeliveryModalOpen}
        onClose={handleCloseModal}
        options={deliveryOptions}
        selectedId={selectedDeliveryId}
        onSelect={selectDelivery}
        onAddAddress={handleAddAddressClick}
        isAdding={isAddingAddress}
        isFetchingLocation={isFetchingLocation}
        newAddressName={newAddressName}
        onNewAddressNameChange={setNewAddressName}
        onCreateAddress={handleCreateAddress}
        isSavingAddress={isSavingAddress}
        onFinalize={handleSaveDelivery}
        locationError={locationError}
        addressSaveError={addressSaveError}
      />
    </ClientLayout>
  );
};

export default Cart; 